import { format, isValid, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { type Cell, Workbook, type Worksheet } from 'exceljs';

import type { AgllcBulkDonorScheduleInput } from './schema';

const MAX_HEADER_ROW = 25;
const AGLLC_BREED_MAPPING: Record<string, string> = {
  H: 'HO',
  J: 'JE',
  A: 'AN',
  X: 'XX',
};

const AGLLC_LOCATION_MAPPING: Record<string, string> = {
  ERGS: 'AGGSD',
};

const AGLLC_PREGNANCY_MAPPING: Record<string, boolean> = {
  PREG: true,
  OPEN: false,
};

const AGLLC_SEMEN_TYPE_MAPPING: Record<string, string> = {
  SFF: 'SF F',
  SFM: 'SF M',
  RSF: 'RS F',
  RSM: 'RS M',
  CONV: 'Conv',
};

const AGLLC_COLUMN_MAPPING = {
  ID: 'donorNameToMatch',
  PEN: 'pen',
  REG: 'donorRegistrationNumber',
  CBRD: 'donorBreed',
  BDAT: 'birthDate',
  PSI: 'psi',
  GNM$: 'gnm',
  GCM$: 'gcm',
  RPRO: 'pregnant',
  PROGRAM: 'programName',
  NXTASP: 'opuDate',
  LOCATION: 'opuSite',
  SIR1: 'sire',
  NAAB1: 'naab',
  SIR1PSI: 'sir1Psi',
  SEMENTYPE1: 'semenType',
  SIR2: 'backupSire',
  NAAB2: 'backupNaab',
  SIR2PSI: 'sir2Psi',
  SEMENTYPE2: 'backupSemenType',
} as const satisfies Record<string, keyof AgllcBulkDonorScheduleInput>;

const AGLLC_HEADER_NAMES: Record<keyof typeof AGLLC_COLUMN_MAPPING, string> = {
  ID: 'ID',
  PEN: 'PEN',
  REG: 'REG',
  CBRD: 'CBRD',
  BDAT: 'BDAT',
  PSI: 'PSI',
  GNM$: 'GNM$',
  GCM$: 'GCM$',
  RPRO: 'RPRO',
  PROGRAM: 'PROGRAM',
  NXTASP: 'NXTASP',
  LOCATION: 'LOCATION',
  SIR1: 'SIR1',
  NAAB1: 'NAAB1',
  SIR1PSI: 'SIR1PSI',
  SEMENTYPE1: 'SEMENTYPE1',
  SIR2: 'SIR2',
  NAAB2: 'NAAB2',
  SIR2PSI: 'SIR2PSI',
  SEMENTYPE2: 'SEMENTYPE2',
};

const REQUIRED_HEADERS = Object.keys(AGLLC_COLUMN_MAPPING);

export type ParsedAgllcRoster = {
  rows: AgllcBulkDonorScheduleInput[];
};

export async function parseAgllcRoster(file: File): Promise<ParsedAgllcRoster> {
  const fileName = file.name.toLowerCase();
  if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.csv')) {
    throw new Error('AGLLC rosters must be XLSX or CSV files.');
  }

  const workbook = new Workbook();
  if (fileName.endsWith('.csv')) {
    await loadCsvIntoWorkbook(workbook, file);
  } else {
    await workbook.xlsx.load(await file.arrayBuffer());
  }

  const worksheet = workbook.getWorksheet(1);
  if (!worksheet) {
    throw new Error('No worksheet found.');
  }

  const header = findHeaderRow(worksheet);
  const missingHeaders = REQUIRED_HEADERS.filter(
    (requiredHeader) => !header.columns.has(requiredHeader),
  );
  if (missingHeaders.length > 0) {
    throw new Error(
      `Missing required AGLLC columns: ${missingHeaders
        .map(
          (header) =>
            AGLLC_HEADER_NAMES[header as keyof typeof AGLLC_COLUMN_MAPPING],
        )
        .join(', ')}`,
    );
  }

  const rows: AgllcBulkDonorScheduleInput[] = [];

  for (
    let rowNumber = header.rowNumber + 1;
    rowNumber <= worksheet.rowCount;
    rowNumber++
  ) {
    const sourceRow = worksheet.getRow(rowNumber);
    const row: Partial<AgllcBulkDonorScheduleInput> = {};
    let hasData = false;

    for (const [sourceHeader, field] of Object.entries(AGLLC_COLUMN_MAPPING)) {
      const columnNumber = header.columns.get(sourceHeader)!;
      const cell = sourceRow.getCell(columnNumber);
      const value = readCellValue(cell, field);

      if (value !== undefined) {
        row[field] = value as never;
        hasData = true;
      }
    }

    if (hasData) {
      rows.push(row as AgllcBulkDonorScheduleInput);
    }
  }

  return { rows };
}

function findHeaderRow(worksheet: Worksheet) {
  let bestMatch = {
    rowNumber: 1,
    columns: new Map<string, number>(),
  };

  const lastCandidateRow = Math.min(worksheet.rowCount, MAX_HEADER_ROW);
  for (let rowNumber = 1; rowNumber <= lastCandidateRow; rowNumber++) {
    const columns = new Map<string, number>();
    worksheet
      .getRow(rowNumber)
      .eachCell({ includeEmpty: true }, (cell, col) => {
        const header = normalizeHeader(cell.text);
        if (header in AGLLC_COLUMN_MAPPING) {
          columns.set(header, col);
        }
      });

    if (columns.size > bestMatch.columns.size) {
      bestMatch = { rowNumber, columns };
    }
    if (columns.size === REQUIRED_HEADERS.length) {
      return bestMatch;
    }
  }

  return bestMatch;
}

function normalizeHeader(value: string) {
  return value.trim().replace(/\s+/g, '').toUpperCase();
}

function readCellValue(
  cell: Cell,
  field: keyof AgllcBulkDonorScheduleInput,
): string | number | boolean | undefined {
  const displayedValue = readDisplayedValue(cell);
  if (
    cell.value === null ||
    cell.value === undefined ||
    displayedValue === ''
  ) {
    return undefined;
  }

  if (field === 'birthDate' || field === 'opuDate') {
    if (cell.value instanceof Date) {
      return formatInTimeZone(cell.value, 'UTC', 'yyyy-MM-dd');
    }
    return normalizeDateString(displayedValue) ?? displayedValue;
  }

  if (field === 'donorBreed') {
    const breed = displayedValue.toUpperCase();
    return AGLLC_BREED_MAPPING[breed] ?? breed;
  }

  if (field === 'opuSite') {
    const location = displayedValue.toUpperCase();
    return AGLLC_LOCATION_MAPPING[location] ?? displayedValue;
  }

  if (field === 'pregnant') {
    const status = displayedValue.toUpperCase();
    return AGLLC_PREGNANCY_MAPPING[status];
  }

  if (field === 'semenType' || field === 'backupSemenType') {
    const key = displayedValue.toUpperCase();
    return AGLLC_SEMEN_TYPE_MAPPING[key] ?? displayedValue;
  }

  if (
    field === 'psi' ||
    field === 'gnm' ||
    field === 'gcm' ||
    field === 'sir1Psi' ||
    field === 'sir2Psi'
  ) {
    const num = Number(displayedValue);
    return Number.isNaN(num) ? undefined : num;
  }

  return displayedValue;
}

function readDisplayedValue(cell: Cell) {
  if (typeof cell.value === 'number' && /^0+$/.test(cell.numFmt)) {
    return String(cell.value).padStart(cell.numFmt.length, '0');
  }
  return cell.text.trim();
}

const CSV_DATE_FORMATS = ['yyyy-MM-dd', 'M/d/yyyy', 'M-d-yyyy'];

function normalizeDateString(value: string): string | undefined {
  for (const pattern of CSV_DATE_FORMATS) {
    const parsed = parse(value, pattern, new Date());
    if (isValid(parsed)) {
      return format(parsed, 'yyyy-MM-dd');
    }
  }
  return undefined;
}

async function loadCsvIntoWorkbook(workbook: Workbook, file: File) {
  const text = await file.text();
  const rows = parseCsvText(text);
  const worksheet = workbook.addWorksheet('Sheet1');
  for (const row of rows) {
    worksheet.addRow(row);
  }
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      pushField();
    } else if (ch === '\r') {
      if (text[i + 1] === '\n') i++;
      pushRow();
    } else if (ch === '\n') {
      pushRow();
    } else {
      field += ch;
    }
  }

  if (field.length > 0 || row.length > 0) {
    pushRow();
  }

  return rows.filter((r) => r.length > 1 || r[0]?.trim() !== '');
}
